import React, { useEffect, useRef, useState } from "react";
import { Avatar, Button, Grid, LinearProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, ClickAwayListener } from "@material-ui/core";
import { Info as InfoIcon } from "@material-ui/icons";
import { Upload } from "antd";
import Icon from "@ant-design/icons";
import { RcFile } from "antd/lib/upload";
import { Club } from "api/clubs";
import { GridTextField } from "components";
import { useSnackbar } from "notistack";
import { CompanyProfile, getCompanyProfile, patchCompanyProfile, postCompanyProfile, updateCompanyProfilePassword } from "api/company";
import { ErrorTypes, isValidPassword } from "../../../../utils/helpers";
import i18n from "../i18n";
import "antd/dist/antd.css";
import "../b2bfeaturesStyle.scss";

interface CompanyProfileViewProps {
    club: Club
}

const CompanyProfileView = ({ club }: CompanyProfileViewProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );
    const displayCustomError = (error: any) => enqueueSnackbar(
        `${i18n.warningMsgForPassword}`,
        { variant: "error" },
    );

    const IMG_FILESIZE = 3000000;
    const VIDEO_FILESIZE = 100000000;
    const MAX_GALLERY_UPLOAD = 5;
    const VALID_NAME_LENGTH = 50;
    const VALID_STATUS_LENGTH = 144;
    const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
    const [isLoading, setLoading] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [statusText, setStatusText] = useState("");
    const [mediaName, setMediaName] = useState("About Me");
    const [profilePassword, setProfilePassword] = useState("");
    const [mediaVideo, setMediaVideo] = useState<File>();
    const [mediaPicture, setMediaPicture] = useState<File>();
    const inputPicRef = useRef<HTMLInputElement>(null);
    const inputMediaVideoRef = useRef<HTMLInputElement>(null);
    const inputMediaPicRef = useRef<HTMLInputElement>(null);
    const [showSavePasswordDialog, setShowSavePasswordDialog] = useState(false);
    const [retrivedPassword, setRetrivedPassword] = useState("");
    const [openMonthlyBudgetToolTip, setOpenMonthlyBudgetToolTip] = useState(false);
    const [showVideoDialog, setShowVideoDialog] = useState(false);

    const handleTooltipCloseForMonthlyBudget = () => {
        setOpenMonthlyBudgetToolTip(false);
    };
    const handleTooltipOpenForMonthlyBudget = () => {
        setOpenMonthlyBudgetToolTip(true);
    };

    const fetchCompanyProfile = () => {
        getCompanyProfile({ clubId: club!.id }).then((response) => {
            setCompanyProfile(response);
            if (response) {
                setFirstName(response.firstName);
                setStatusText(response.statusText);
            }
        }).catch((err) => {
            if (err.type === ErrorTypes.COMPANY_PROFILE_NOT_FOUND) {
                setCompanyProfile(null);
            } else if (err.type === ErrorTypes.CLUB_IN_DEMO_MODE) {
                enqueueSnackbar(i18n.demoModeErrorMsg, { variant: "error" });
            } else {
                setCompanyProfile(null);
                if (err.type !== ErrorTypes.COMPANY_HAS_NO_ACTIVE_SUBSCRIPTION) {
                    displayError(err);
                }
            }
        });
    };

    const addCompanyProfile = () => {
        setLoading(true);
        postCompanyProfile({ clubId: club!.id }).then((res: any) => {
            getCompanyProfile({ clubId: club!.id }).then((profileRes) => {
                setLoading(false);
                setCompanyProfile(profileRes);
                setShowSavePasswordDialog(true);
                setRetrivedPassword(res.password);
            }).catch((error) => {
                setLoading(false);
                displayError(error);
            });
        }).catch((error) => {
            setLoading(false);
            displayError(error);
        });
    };

    useEffect(() => {
        if (club) {
            fetchCompanyProfile();
        }
    }, [club]);

    // change profile Picture
    const handleUploadProfilePicture = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files![0];
        const allowedMime = ["image/png", "image/jpg", "image/jpeg", "image/gif"];
        if (file) {
            if (file.size > IMG_FILESIZE) {
                enqueueSnackbar(i18n.fileTooLarge, { variant: "error" });
            } else if (!allowedMime.includes(file.type)) {
                enqueueSnackbar(i18n.invalidImageFile, { variant: "error" });
            } else {
                try {
                    const response = await patchCompanyProfile({
                        clubId: club.id,
                        profilePicture: file,
                    });
                    if (response) {
                        fetchCompanyProfile(); // refresh company profiles
                        enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
                    }
                } catch (error) {
                    displayError(error);
                }
            }
        }
    };

    const handleMediaVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files![0];
        const allowedMime = ["video/quicktime", "video/mp4", "video/wmv"];
        if (file) {
            if (file.size > VIDEO_FILESIZE) {
                enqueueSnackbar(i18n.videoFileTooLarge, { variant: "error" });
            } else if (!allowedMime.includes(file.type)) {
                enqueueSnackbar(i18n.invalidVideoFile, { variant: "error" });
            } else {
                setMediaVideo(file);
            }
        }
    };
    const handleMediaPicture = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files![0];
        const allowedMime = ["image/png", "image/jpg", "image/jpeg", "image/gif"];
        if (file) {
            if (file.size > IMG_FILESIZE) {
                enqueueSnackbar(i18n.fileTooLarge, { variant: "error" });
            } else if (!allowedMime.includes(file.type)) {
                enqueueSnackbar(i18n.invalidImageFile, { variant: "error" });
            } else {
                setMediaPicture(file);
            }
        }
    };
    const saveProfileData = async () => {
        try {
            setLoading(true);
            const response = await patchCompanyProfile({
                clubId: club.id,
                firstName,
                statusText,
            });
            if (response) {
                fetchCompanyProfile(); // refresh company profiles
                enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
                setLoading(false);
            }
        } catch (error) {
            displayError(error);
        }
    };
    const saveMediaItemData = async () => {
        try {
            setLoading(true);
            const response = await patchCompanyProfile({
                clubId: club.id,
                mediaItemName: mediaName || "About Me",
                mediaItem: mediaVideo,
                mediaItemThumbnail: mediaPicture,
            });
            setShowVideoDialog(false);
            if (response) {
                fetchCompanyProfile(); // refresh company profiles
                enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
                setLoading(false);
            }
        } catch (error) {
            displayError(error);
        }
    };

    // remove media item
    const removeMediaItem = async (removeMediaItemName: string) => {
        try {
            setLoading(true);
            const response = await patchCompanyProfile({
                clubId: club.id,
                removeMediaItemName,
            });
            if (response) {
                fetchCompanyProfile();
                enqueueSnackbar(i18n.deletedSuccessfully, { variant: "success" });
            }
        } catch (error) {
            displayError(error);
        } finally {
            setLoading(false);
        }
    };
    // remove club logo
    const handleRemoveLogo = async () => {
        try {
            const response = await patchCompanyProfile({
                clubId: club.id,
                deleteProfilePicture: true,
            });
            if (response) {
                fetchCompanyProfile(); // refresh company profiles
                enqueueSnackbar(i18n.deletedSuccessfully, { variant: "success" });
            }
        } catch (error) {
            displayError(error);
        }
    };

    // update password
    const updateProfilePassword = async () => {
        try {
            const response = await updateCompanyProfilePassword({
                clubId: club.id,
                password: profilePassword,
            });
            if (response) {
                fetchCompanyProfile(); // refresh company profiles
                enqueueSnackbar(i18n.deletedSuccessfully, { variant: "success" });
            }
        } catch (error: any) {
            if (error.type === ErrorTypes.INVALID_PASSWORD_FORMAT || error.type === ErrorTypes.UN_KNOWN_AUTH_ERROR) {
                displayCustomError(error);
            } else {
                displayError(error);
            }
        }
    };

    return (
        <>
            <Grid container>
                <Grid item xs={12} md={12}>
                    <Typography align="left" variant="h6">{i18n.profileInformation}</Typography>
                </Grid>
                <Grid item xs={12} md={12}>
                    <Typography align="left">{i18n.profilePageMessage}</Typography>
                </Grid>
                {companyProfile === null && club.clubStatus !== "demo" && (
                    <Grid item xs={12} className="companyProile">
                        <Button
                            color="primary"
                            disabled={isLoading}
                            variant="contained"
                            onClick={addCompanyProfile}
                        >
                            {i18n.addCompanyProfile}
                        </Button>
                    </Grid>
                )}
                {companyProfile !== null && (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12} className="club_display_center">
                                <Avatar src={companyProfile?.profilePictureUrl} className="club_avatar" />
                            </Grid>
                            <input accept="image/*" ref={inputPicRef} className="club_hide" type="file" onChange={handleUploadProfilePicture} />
                            <Grid item xs={12} className="club_display_center">
                                <Button color="primary" variant="contained" onClick={() => inputPicRef.current!.click()} disabled={isLoading}>
                                    {i18n.change}
                                </Button>
                                &nbsp;
                                <Button variant="contained" onClick={handleRemoveLogo} disabled={isLoading} className="club_left_8 removeButton">
                                    {i18n.remove}
                                </Button>
                            </Grid>

                            <Grid item xs={12}>
                                {isLoading && <LinearProgress />}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <GridTextField
                                    error={Number(firstName.length || 0) > VALID_NAME_LENGTH}
                                    helperText={`${Number(firstName.length || 0)} / ${VALID_NAME_LENGTH}`}
                                    maxLength={VALID_NAME_LENGTH}
                                    label={i18n.profileName}
                                    size={12}
                                    value={firstName || ""}
                                    onChange={(v) => setFirstName(v)}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <GridTextField
                                    error={Number(statusText.length || 0) > VALID_STATUS_LENGTH}
                                    helperText={`${Number(statusText.length || 0)} / ${VALID_STATUS_LENGTH}`}
                                    maxLength={VALID_STATUS_LENGTH}
                                    label={i18n.status}
                                    size={12}
                                    value={statusText || ""}
                                    onChange={(v) => setStatusText(v)}
                                />
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={12} style={{ textAlign: "center" }}>
                                    <Button
                                        color="primary"
                                        className="btn"
                                        disabled={isLoading}
                                        variant="contained"
                                        onClick={saveProfileData}
                                    >
                                        {i18n.save}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                        {companyProfile && (
                            <>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>&nbsp;</Grid>
                                    <Grid item xs={12} md={12}>
                                        <Typography align="left" variant="h6">
                                            {`${i18n.galleryMedia} (${(companyProfile.galleryPictureUrls || []).length}/${MAX_GALLERY_UPLOAD})`}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <Upload
                                            action={(file: RcFile) => { patchCompanyProfile({ clubId: club.id, galleryPicture: file }).then(fetchCompanyProfile); return ""; }}
                                            fileList={companyProfile.galleryPictureUrls.map((url: string, i: number) => ({ uid: String(i), status: "done", url, name: "", size: i, type: "" }))}
                                            listType="picture-card"
                                            onChange={(info) => { if (info.file.status === "removed") { patchCompanyProfile({ clubId: club.id, deleteGalleryPictureIndex: Number(info.file.uid) }).then(fetchCompanyProfile).catch((error) => displayError(error)); } }}
                                        >
                                            {(companyProfile.galleryPictureUrls || []).length < MAX_GALLERY_UPLOAD && (
                                                <div>
                                                    <Icon type="plus" />
                                                    <div className="ant-upload-text">{i18n.upload}</div>
                                                </div>
                                            )}
                                        </Upload>
                                    </Grid>

                                </Grid>

                                {/* displaying list of media items */}
                                <Grid container spacing={2}>
                                    {/* display media items */}
                                    <Grid item xs={12} md={4}>&nbsp;</Grid>
                                    <Grid item xs={12} md={12}>
                                        <Typography align="left" variant="h6">{i18n.mediaItem}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <div className="customGalleryItem">
                                            <Upload
                                                fileList={companyProfile.mediaItems.map((m) => ({ uid: m.name, status: "done", url: m.thumbnailUrl, name: m.name, size: 0, type: "" }))}
                                                listType="picture-card"
                                                className="upload_custom"
                                                onChange={(info) => { if (info.file.status === "removed") { removeMediaItem(info.file.uid).then(fetchCompanyProfile); } }}
                                            />
                                            {(companyProfile.mediaItems || []).length < MAX_GALLERY_UPLOAD && (
                                                <Button className="customPlusBtn" onClick={() => setShowVideoDialog(true)}>
                                                    <div className="ant-upload ant-upload-select ant-upload-select-picture-card">
                                                        <span className="ant-upload" role="button">
                                                            <div>
                                                                <i aria-label="icon: plus" className="anticon anticon-plus">
                                                                    <svg viewBox="64 64 896 896" focusable="false" className="" data-icon="plus" width="1em" height="1em" fill="currentColor" aria-hidden="true">
                                                                        <path d="M482 152h60q8 0 8 8v704q0 8-8 8h-60q-8 0-8-8V160q0-8 8-8z">&mnsp;</path>
                                                                        <path d="M176 474h672q8 0 8 8v60q0 8-8 8H176q-8 0-8-8v-60q0-8 8-8z">&mnsp;</path>
                                                                    </svg>
                                                                </i>
                                                                <div className="ant-upload-text">
                                                                    {i18n.upload}
                                                                </div>
                                                            </div>
                                                        </span>
                                                    </div>
                                                </Button>
                                            )}
                                        </div>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} md={4}>&nbsp;</Grid>
                                <Grid item xs={12} md={12}>
                                    <Typography> </Typography>
                                </Grid>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={12}>
                                        <Typography align="left" variant="h6">{i18n.changeProfilePassword}</Typography>
                                    </Grid>

                                    <Grid item xs={11} md={4}>
                                        <GridTextField
                                            label={i18n.enterPassword}
                                            size={12}
                                            value={profilePassword || ""}
                                            error={profilePassword !== "" && isValidPassword(profilePassword)}
                                            helperText={i18n.warningMsgForPassword}
                                            onChange={(v) => setProfilePassword(v)}
                                        />
                                    </Grid>
                                    <Grid item xs={1} md={1}>
                                        <ClickAwayListener onClickAway={handleTooltipCloseForMonthlyBudget}>
                                            <div>
                                                <Tooltip
                                                    PopperProps={{
                                                        disablePortal: true,
                                                    }}
                                                    onClose={handleTooltipCloseForMonthlyBudget}
                                                    open={openMonthlyBudgetToolTip}
                                                    placement="bottom-end"
                                                    disableFocusListener
                                                    disableHoverListener
                                                    disableTouchListener
                                                    title={i18n.warningMsgForPassword}
                                                    arrow
                                                >
                                                    <InfoIcon onClick={handleTooltipOpenForMonthlyBudget} color="secondary" fontSize="small" style={{ margin: "18px 0" }} />
                                                </Tooltip>
                                            </div>
                                        </ClickAwayListener>
                                    </Grid>
                                    <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
                                        <Button
                                            color="primary"
                                            className="btn"
                                            disabled={isLoading || isValidPassword(profilePassword)}
                                            variant="contained"
                                            onClick={updateProfilePassword}
                                        >
                                            {i18n.updatePwd}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </>
                )}
            </Grid>

            <Dialog open={showSavePasswordDialog} onClose={() => setShowSavePasswordDialog(false)}>
                <DialogTitle>{i18n.savePassword}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography>{i18n.savePasswordMessage}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography align="center" variant="h6">{retrivedPassword}</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setShowSavePasswordDialog(false)}>{i18n.close}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showVideoDialog} onClose={() => setShowVideoDialog(false)}>
                <DialogTitle>{i18n.mediaItemTitle}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <GridTextField
                                    label={i18n.mediaName}
                                    size={12}
                                    value={mediaName}
                                    onChange={(v) => setMediaName(v)}
                                />
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <Typography align="left" variant="h6">{i18n.uploadVideo}</Typography>
                                <input accept="video/*" ref={inputMediaVideoRef} type="file" onChange={handleMediaVideo} />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Typography align="left" variant="h6">{i18n.uploadThumbnail}</Typography>
                                <input accept="image/*" ref={inputMediaPicRef} type="file" onChange={handleMediaPicture} />
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className="justifyCenter">
                    <Button
                        color="primary"
                        className="btn"
                        disabled={isLoading}
                        variant="contained"
                        onClick={saveMediaItemData}
                    >
                        {i18n.save}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CompanyProfileView;
